import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { supabase } from '@/integrations/supabase/client';
import { useTranslation } from 'react-i18next';
import { LoginLanguageSwitcher } from '@/components/LoginLanguageSwitcher';
import { LogOut } from 'lucide-react';

// Verify page: invokes sign-up function and redirects on success.
export default function Verify() {
  const [error, setError] = useState<string | null>(null);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation();
  
  const handleLogout = async () => {
    try {
      setIsLoggingOut(true);
      await supabase.auth.signOut();
      navigate('/login', { replace: true });
    } catch (err) {
      console.error('Logout failed:', err);
      setIsLoggingOut(false);
    }
  };

  useEffect(() => {
    const signUp = async () => {
      try {
        // Get current session
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) throw new Error('Not authenticated');
        
        // Call edge function
        const { error: fnError } = await supabase.functions.invoke('sign-up', {
          headers: { Authorization: `Bearer ${session.access_token}` },
        });
        
        if (fnError) {
          // Handle subscription error
          if (fnError.message.includes('Edge Function returned a non-2xx status code')) {
            setError('SUBSCRIPTION_ERROR');
            return;
          }
          throw fnError;
        }
        
        // Redirect to homepage on success
        navigate('/', { replace: true });
      } catch (err: unknown) {
        // Handle other errors
        if (typeof err === 'object' && err !== null && 'message' in err) {
          setError((err as Error).message);
        } else {
          setError(t('success.error.signUpFailed'));
        }
      }
    };
    signUp();
  }, [navigate, t]);

  // Render error state
  if (error) {
    const isSubscriptionError = error === 'SUBSCRIPTION_ERROR';
    const errorTitle = isSubscriptionError 
      ? t('success.error.noSubscription.title')
      : t('success.error.signUpFailed');
    
    const errorDescription = isSubscriptionError
      ? t('success.error.noSubscription.description')
      : error;
    
    // Simple function to render markdown-style links
    const renderDescription = (text: string) => {
      if (!text.includes('[') || !text.includes('](/')) return text;
      
      const linkMatch = text.match(/\[(.*?)\]\(([^)]+)\)/);
      if (!linkMatch) return text;
      
      const [fullMatch, linkText, linkUrl] = linkMatch;
      const parts = text.split(fullMatch);
      
      return (
        <>
          {parts[0]}
          <a href={linkUrl} className="text-primary hover:underline">{linkText}</a>
          {parts[1]}
        </>
      );
    };
    
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4 relative">
        <LoginLanguageSwitcher />
        <div className="text-center">
          <h1 className="text-2xl font-semibold mb-4 text-red-600">{errorTitle}</h1>
          <p className="text-gray-600 mb-6">{renderDescription(errorDescription)}</p>
          <button
            onClick={handleLogout}
            disabled={isLoggingOut}
            className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 hover:bg-gray-50 rounded-md transition-colors text-gray-800 font-medium shadow-sm"
          >
            <LogOut className="h-4 w-4" />
            {t('menu.logout')}
          </button>
        </div>
      </div>
    );
  }
  
  // Render loading state
  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4 relative">
      <LoginLanguageSwitcher />
      <div className="text-center">
        <div className="flex justify-center mb-6">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary" />
        </div>
        <h1 className="text-2xl font-semibold mb-4">{t('success.verifying.title')}</h1>
        <p className="text-gray-600 mb-6">{t('success.verifying.description')}</p>
        <button
          onClick={handleLogout}
          disabled={isLoggingOut}
          className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 hover:bg-gray-50 rounded-md transition-colors text-gray-800 font-medium shadow-sm"
        >
          <LogOut className="h-4 w-4" />
          {t('menu.logout')}
        </button>
      </div>
    </div>
  );
}
